import {
	BaseModal,
	ConfirmModal,
	FormActions,
	FormField,
	FormInput,
	FormSectionTitle,
	FormSelect,
	useConfirmModal,
} from '@practium/ui';
import { FERTILIZER_PACK_SLOTS } from '../../constants/maintenance.constants';
import {
	useFertilizerPackForm,
	type FertilizerPackModalMode,
} from '../../hooks/useFertilizerPackForm';
import type { FertilizerPack } from '@practium/features/api';
import styles from './FertilizerPackModal.module.css';

interface FertilizerPackModalProps {
	isOpen: boolean;
	mode: FertilizerPackModalMode;
	pack?: FertilizerPack | null;
	onClose: () => void;
	onSaved: (pack: FertilizerPack) => void;
	onDeleted?: () => void;
}

export const FertilizerPackModal = ({
	isOpen,
	mode,
	pack,
	onClose,
	onSaved,
	onDeleted,
}: FertilizerPackModalProps) => {
	const { form, errors, set, isView, isLoading, optionsBySlot, slotLabel, handleSubmit, handleDelete } =
		useFertilizerPackForm({ isOpen, mode, pack, onClose, onSaved, onDeleted });
	const {
		confirm,
		isOpen: isConfirmOpen,
		options: confirmOptions,
		handleConfirm,
		handleCancel,
	} = useConfirmModal();

	const confirmDelete = async () => {
		const confirmed = await confirm({
			title: 'Excluir pacote',
			message: 'Tem certeza que deseja excluir este pacote de produtos? Esta ação não pode ser desfeita.',
			confirmText: 'Excluir',
			cancelText: 'Cancelar',
			variant: 'danger',
		});
		if (confirmed) await handleDelete();
	};

	const title =
		mode === 'edit' ? 'Editar pacote' : mode === 'view' ? 'Visualizar pacote' : 'Novo pacote';

	return (
		<>
			<BaseModal
				isOpen={isOpen}
				onClose={onClose}
				title={title}
				wide
				isLoading={isLoading}
				actions={
					<FormActions
						onCancel={mode === 'edit' ? undefined : onClose}
						onDelete={mode === 'edit' ? confirmDelete : undefined}
						deleteInActions={mode === 'edit'}
						onSave={isView ? undefined : handleSubmit}
						isLoading={isLoading}
					/>
				}
			>
				<div className={styles.form}>
					<div className={styles.section}>
						<FormSectionTitle>Identificação</FormSectionTitle>
						<div className={styles.sectionFields}>
							<FormField label="Nome do pacote" required error={errors.name}>
								{isView ? (
									<span className={styles.viewText}>{form.name || '—'}</span>
								) : (
									<FormInput
										placeholder="Ex.: Pacote Café Arábica - 2026"
										value={form.name ?? ''}
										onChange={(e) => set('name', e.target.value)}
										hasError={!!errors.name}
									/>
								)}
							</FormField>
						</div>
					</div>
					<div className={styles.section}>
						<FormSectionTitle>Fertilizantes</FormSectionTitle>
						<div className={styles.slotsGrid}>
							{FERTILIZER_PACK_SLOTS.map((slot) => (
								<FormField key={slot.key} label={slot.label}>
									{isView ? (
										<span className={styles.viewText}>{slotLabel(slot.key, form[slot.key])}</span>
									) : (
										<FormSelect
											value={form[slot.key] || ''}
											onChange={(value) => set(slot.key, value)}
											options={optionsBySlot[slot.key]}
											placeholder="Selecione o fertilizante"
											clearable
											searchable
										/>
									)}
								</FormField>
							))}
						</div>
					</div>
				</div>
			</BaseModal>
			<ConfirmModal
				isOpen={isConfirmOpen}
				title={confirmOptions.title}
				message={confirmOptions.message}
				confirmText={confirmOptions.confirmText}
				cancelText={confirmOptions.cancelText}
				variant={confirmOptions.variant}
				onConfirm={handleConfirm}
				onCancel={handleCancel}
			/>
		</>
	);
};
